import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAccountPricing } from '@/composables/useAccountPricing'
import { useJwtStore } from '@/stores/jwt'
import { usePaygRatesStore } from '@/stores/paygRates'
import { useTaxConfigStore } from '@/stores/taxConfig'
import { VAT_RATE } from '@/constants/calculatorPricing'
import { currencyLocale, formatCurrency, formatEur } from '@/utils/formatCurrency'
import { netRateFormat, shownOnBasis, showsNet } from '@/utils/shownPrice'
import type { PaygRates, SwitchText } from '@/utils/termSwitch'

// How the switch dialogs word amounts for the signed-in viewer: quotes on the
// account's basis, and the net pay-as-you-go rates with VAT unless the viewer
// is shown net or no VAT is charged.
export function useSwitchText() {
  const { t, locale } = useI18n()
  const jwtStore = useJwtStore()
  const taxConfigStore = useTaxConfigStore()
  const { pricing } = useAccountPricing()
  void taxConfigStore.fetchConfig()

  const isOrganization = computed(() => jwtStore.getJwtContent?.type === 'organization')

  return computed<SwitchText>(() => {
    const net = showsNet(pricing.value.basis, isOrganization.value)
    const usage = netRateFormat(net, taxConfigStore.isVatExempt ? 0 : VAT_RATE, locale.value)
    return {
      t,
      net,
      vat: t(`common.termSwitch.vat.${net ? 'net' : 'gross'}`),
      money: (cents) => formatEur(shownOnBasis(cents, pricing.value.basis, isOrganization.value, VAT_RATE) / 100, locale.value),
      rate: usage.rate,
      netMoney: usage.money,
      unitPrice: (cents) => {
        const shown = pricing.value.basis === 'gross' && isOrganization.value ? cents / (1 + VAT_RATE) : cents
        return formatCurrency(shown / 100, 'EUR', currencyLocale(locale.value), 4)
      }
    }
  })
}

// The net pay-as-you-go rates of a Kubernetes namespace, as the order page
// reads them; the published defaults until they load or when they cannot.
export function usePaygRates() {
  const store = usePaygRatesStore()
  const rates = computed<PaygRates>(() => store.rates)
  return { rates, load: store.load }
}
